import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { adminGenerateArticle, formatApiErrorDetail } from "../../lib/api";
import { CATEGORIES } from "../../lib/categories";
import { ArrowLeft, Sparkles, ExternalLink } from "lucide-react";
import MetaTags from "../../lib/MetaTags";

export default function AIGenerator() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [topic, setTopic] = useState("");
  const [category, setCategory] = useState(CATEGORIES[0].slug);
  const [keywords, setKeywords] = useState("");
  const [tone, setTone] = useState("informative, helpful, local Chicago perspective");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!loading && !user) navigate("/admin21");
  }, [user, loading, navigate]);

  const generate = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    setResult(null);
    try {
      const r = await adminGenerateArticle({
        topic,
        category,
        target_keywords: keywords.split(",").map((k) => k.trim()).filter(Boolean),
        tone,
      });
      setResult(r);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Generation failed. Check that EMERGENT_LLM_KEY is configured and try again.");
    } finally {
      setBusy(false);
    }
  };

  if (loading || !user) return null;

  return (
    <div className="min-h-screen bg-[#F4F4F0]">
      <MetaTags title="AI Article Generator" />
      <header className="bg-[#121212] text-white">
        <div className="ceg-container py-4 flex items-center justify-between">
          <Link to="/admin" className="text-sm flex items-center gap-2 hover:text-[#FF3823]"><ArrowLeft size={16} /> Back to admin</Link>
          <span className="text-xs uppercase tracking-wider flex items-center gap-2"><Sparkles size={14} /> AI Generator</span>
        </div>
      </header>

      <section className="ceg-container py-10 grid grid-cols-12 gap-6">
        <form onSubmit={generate} className="col-span-12 md:col-span-7 bg-white border border-[#121212] p-6 space-y-4" data-testid="ai-generator-form">
          <p className="ceg-overline">Generate SEO article</p>
          <h1 className="font-['Outfit'] font-black tracking-tighter text-3xl">Spin up a new guide.</h1>
          <p className="text-sm text-[#5C5C5C]">Uses Claude Sonnet via the universal LLM key to produce a long-form, SEO-optimized article with FAQs. Always review before relying on the content publicly.</p>

          {error && <div className="p-3 border-l-4 border-[#FF3823] bg-red-50 text-sm" data-testid="ai-error">{error}</div>}

          <div>
            <label className="ceg-overline block mb-2">Topic</label>
            <input required value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="e.g. Sell a Nintendo Switch in Chicago" className="w-full border border-[#121212] px-3 py-2 outline-none focus:border-[#0055FF]" data-testid="ai-topic" />
          </div>
          <div>
            <label className="ceg-overline block mb-2">Category</label>
            <select value={category} onChange={(e) => setCategory(e.target.value)} className="w-full border border-[#121212] px-3 py-2 outline-none" data-testid="ai-category">
              {CATEGORIES.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="ceg-overline block mb-2">Target keywords (comma sep)</label>
            <input value={keywords} onChange={(e) => setKeywords(e.target.value)} placeholder="nintendo switch, chicago, sell" className="w-full border border-[#121212] px-3 py-2 outline-none focus:border-[#0055FF]" data-testid="ai-keywords" />
          </div>
          <div>
            <label className="ceg-overline block mb-2">Tone</label>
            <input value={tone} onChange={(e) => setTone(e.target.value)} className="w-full border border-[#121212] px-3 py-2 outline-none focus:border-[#0055FF]" data-testid="ai-tone" />
          </div>

          <button type="submit" disabled={busy} className="bg-[#FF3823] text-white px-5 py-3 font-semibold uppercase tracking-wider text-sm hover:bg-[#cc2c1a] disabled:opacity-60 flex items-center gap-2" data-testid="ai-generate-submit">
            <Sparkles size={16} /> {busy ? "Generating (30–60s)..." : "Generate article"}
          </button>
        </form>

        <aside className="col-span-12 md:col-span-5 space-y-4">
          <div className="bg-[#121212] text-white p-6">
            <p className="ceg-overline text-white">How it works</p>
            <ol className="mt-3 space-y-2 text-sm text-white/80 list-decimal pl-5">
              <li>Specify the topic, category, and target keywords</li>
              <li>Claude generates ~1100-1500 words with H2/H3 structure and FAQs</li>
              <li>The article is auto-saved as a draft (not featured by default)</li>
              <li>Open it in the editor to review, edit, and mark featured</li>
            </ol>
          </div>

          {result && (
            <div className="bg-white border border-[#121212] p-5" data-testid="ai-result">
              <p className="ceg-overline">✓ Generated</p>
              <h3 className="font-['Outfit'] font-bold text-lg mt-2">{result.title}</h3>
              <p className="text-xs text-[#5C5C5C] mt-1">/{result.slug}</p>
              <div className="mt-4 flex gap-3">
                <Link to={`/admin/articles/${result.id}/edit`} className="ceg-btn-primary text-xs py-2 px-3" data-testid="ai-result-edit">Edit article</Link>
                <a href={`/article/${result.slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs uppercase tracking-wider underline">
                  Preview <ExternalLink size={12} />
                </a>
              </div>
            </div>
          )}
        </aside>
      </section>
    </div>
  );
}
